// React Query hooks for Activity Center & Notifications

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMissedSummary,
  getMyActivity,
  getTeamFeed,
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '@/services/activityCenterService';

// ── Query keys ──

export const activityKeys = {
  missed: ['activity-center', 'missed'] as const,
  myActivity: (params: Record<string, unknown>) => ['activity-center', 'my-activity', params] as const,
  feed: (params: Record<string, unknown>) => ['activity-center', 'feed', params] as const,
};

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (params: Record<string, unknown>) => ['notifications', 'list', params] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
};

// ── Activity Center hooks ──

export function useMissedSummary() {
  return useQuery({
    queryKey: activityKeys.missed,
    queryFn: () => getMissedSummary(),
    staleTime: 5 * 60 * 1000, // 5 min — doesn't change often
  });
}

export function useMyActivity(params: {
  skip?: number;
  limit?: number;
  date_from?: string;
  date_to?: string;
} = {}) {
  return useQuery({
    queryKey: activityKeys.myActivity(params),
    queryFn: () => getMyActivity(params),
  });
}

export function useTeamFeed(params: {
  skip?: number;
  limit?: number;
  date_from?: string;
  date_to?: string;
  action_types?: string;
  entity_type?: string;
} = {}) {
  return useQuery({
    queryKey: activityKeys.feed(params),
    queryFn: () => getTeamFeed(params),
  });
}

// ── Notification hooks ──

export function useNotifications(params: {
  is_read?: boolean;
  skip?: number;
  limit?: number;
} = {}) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => getNotifications(params),
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount,
    queryFn: getUnreadCount,
    refetchInterval: 60_000, // poll every 60s for ambient badge
    staleTime: 30_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
